import * as React from 'react';
import { Skeleton } from '@chess/api-skeleton';
import { FilterAnchor, Pie, TextSkeleton, type CardProps, type FilterLink, type PieSlice } from '@chess/api-insights/charts';
import type { GameFilter } from '@chess/api-model';
import { ENDING_CASE_VIEWS, LOSSES, type EndingCase } from '@chess/api-ending-case';
import { EndingCaseLegend } from '@chess/api-ending-case/react';
import { CardAside, CardEmpty, CardNote } from '@chess/api-dashboard/react';
import { lossesOf, type EndingCasesResult } from './EndingCasesAggregator.ts';
import './ending-conversion.css';

// Your losses: every scanned game of the period that reached an ending and
// was lost or let slip, in the first case that matches: slipped (stood
// winning, not won), missed (a winning line passed over, never winning),
// lost on time, lost. Won endings and plain draws (no winning chance) are
// not counted; a draw after a lost chance is, as Slipped or Missed. Every
// slice and legend entry opens exactly those games in the library, where
// each move waits in the ending's moments table with its puzzle. Drawn from
// props only.

export type EndingConversionCardProps = CardProps<{ cases: EndingCasesResult }, {
  /** Opens the library filtered to a slice's games. */
  link?: FilterLink;
}>;

const games = (n: number) => `${n} ${n === 1 ? 'game' : 'games'}`;
const SIZE = 96;
const pct = (n: number, of: number) => `${Math.round((100 * n) / of)}%`;

const TITLES: Record<EndingCase, (n: string) => string> = {
  won: (n) => `${n} won`,
  time: (n) => `${n} lost on time · opens those games`,
  slipped: (n) => `${n} where you stood winning and did not win · opens those games`,
  missed: (n) => `${n} where a winning move went unplayed · opens those games`,
  lost: (n) => `${n} lost with no winning chance in the ending · opens those games`,
  drawn: (n) => `${n} drawn with no winning chance in the ending`,
};

/** The card's slices: the cases not won, in the cases' order and colours (shared with the other ending cards). */
function slicesOf(cases: EndingCasesResult): PieSlice[] {
  return ENDING_CASE_VIEWS.filter((v) => LOSSES.includes(v.case)).map((v) => ({
    key: v.case,
    label: v.label,
    count: cases[v.case],
    colour: v.colour,
    title: TITLES[v.case](games(cases[v.case])),
    filter: v.filter ? ({ blunder: v.filter } as GameFilter) : undefined,
  }));
}

const LossesPie: React.FC<{ slices: PieSlice[]; link?: FilterLink }> = ({ slices, link }) => {
  const sum = slices.reduce((s, x) => s + x.count, 0);
  const byCase = (c: EndingCase) => slices.find((s) => s.key === c)!;
  return (
    <div className="donut-wrap ending-conversion-pie">
      <Pie slices={slices} size={SIZE} link={link} />
      <EndingCaseLegend
        cases={LOSSES}
        valueFor={(v) => `${byCase(v.case).count} · ${pct(byCase(v.case).count, sum)}`}
        labelFor={(v) => {
          const s = byCase(v.case);
          return (
            <FilterAnchor link={s.filter && s.count ? link : undefined} filter={s.filter} title={s.title}>
              {s.label}
            </FilterAnchor>
          );
        }}
      />
    </div>
  );
};

const LoadingPie: React.FC = () => (
  <div className="donut-wrap ending-conversion-pie">
    <Skeleton loading variant="circular" width={`${SIZE / 16}rem`} height={`${SIZE / 16}rem`} label="Loading your losses" />
    <EndingCaseLegend cases={LOSSES} valueFor={() => <Skeleton loading inline variant="text" shape="12 · 34%" />} />
  </div>
);

export const TITLE = 'Your losses';
const ASIDE = 'how they went';

export const EndingConversionCard: React.FC<EndingConversionCardProps> = (props) => {
  if (props.loading)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <CardNote>
          <TextSkeleton sample="12 endings lost or let slip" />
        </CardNote>
        <div className="ending-conversion-body">
          <LoadingPie />
        </div>
      </>
    );
  const { cases, link } = props;
  const total = lossesOf(cases);
  const note = total ? `${total} ${total === 1 ? 'ending' : 'endings'} lost or let slip` : undefined;
  const body = total ? <LossesPie slices={slicesOf(cases)} link={link} /> : <CardEmpty>No lost endings or lost chances in the last 30 days.</CardEmpty>;
  return (
    <>
      <CardAside>{ASIDE}</CardAside>
      {note && <CardNote>{note}</CardNote>}
      <div className="ending-conversion-body">{body}</div>
    </>
  );
};
